/**
 * AB#21519 — why an estimated shipping date was set or moved.
 *
 * MIRRORS App\Enums\ShippingDateChangeReason in the backend `development` repo; the
 * codes and their order must stay in step with it, because the API validates the code
 * against that enum and rejects anything it does not recognise.
 *
 * A fixed list alone was not enough — the order admins asked to be able to write their
 * own reason (Iqra Iqbal, DDI sync 40:10). `other` is that escape hatch: picking it
 * reveals the free-text field and makes it mandatory, because the code carries no
 * meaning on its own and the typed text has to carry it instead.
 */
export interface ShippingDateChangeReason {
  code: string;
  label: string;
  /** When true the free-text detail is mandatory rather than optional. */
  requiresDetail: boolean;
}

export const shippingDateChangeReasons: ShippingDateChangeReason[] = [
  // The first estimate, set when the item goes into production — not a change at all.
  { code: 'initial_estimate', label: 'Initial estimate', requiresDetail: false },
  {
    code: 'factory_capacity',
    label: 'Factory capacity / production backlog',
    requiresDetail: false,
  },
  { code: 'material_shortage', label: 'Material or fabric shortage', requiresDetail: false },
  { code: 'production_delay', label: 'Production delay at factory', requiresDetail: false },
  { code: 'quality_issue', label: 'Quality issue / rework', requiresDetail: false },
  {
    code: 'artwork_or_approval_pending',
    label: 'Artwork or approval pending',
    requiresDetail: false,
  },
  { code: 'shipping_delay', label: 'Shipping or freight delay', requiresDetail: false },
  { code: 'customs_clearance', label: 'Customs clearance', requiresDetail: false },
  { code: 'holiday_closure', label: 'Factory holiday or closure', requiresDetail: false },
  { code: 'customer_requested', label: 'Customer requested a new date', requiresDetail: false },
  { code: 'other', label: 'Other (custom reason)', requiresDetail: true },
];

/** The code whose free text carries the whole meaning. */
export const CUSTOM_SHIPPING_DATE_REASON_CODE = 'other';

/** The minimum a written reason must say to be worth storing. Matches the API's `min:3`. */
export const SHIPPING_DATE_REASON_DETAIL_MIN_LENGTH = 3;

export function findShippingDateChangeReason(
  code?: string | null
): ShippingDateChangeReason | undefined {
  if (!code) return undefined;
  return shippingDateChangeReasons.find((reason) => reason.code === code);
}

/**
 * What the timeline and the activity log should show for one change-log entry.
 *
 * Entries written before AB#21519 carry only a hand-typed `reason` and no code at all,
 * so they render as-is — the history of an order that predates this feature must not
 * come out blank.
 */
export function shippingDateChangeReasonText(entry: {
  reason?: string | null;
  reason_code?: string | null;
  reason_label?: string | null;
  reason_detail?: string | null;
}): string {
  if (!entry) return '';

  const code = entry.reason_code ?? null;
  const detail = (entry.reason_detail ?? '').trim();

  if (code === CUSTOM_SHIPPING_DATE_REASON_CODE) {
    // On the custom option the typed text IS the reason.
    return detail || (entry.reason ?? '').trim();
  }

  const label = entry.reason_label ?? findShippingDateChangeReason(code)?.label ?? '';
  if (label) {
    return detail ? `${label} — ${detail}` : label;
  }

  return (entry.reason ?? '').trim();
}
